import { useColorScheme } from "nativewind";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    Appearance,
    AppState,
    Platform,
    type ColorSchemeName,
} from "react-native";

import {
    resolveColorScheme,
    type AppThemePreference,
    type ResolvedColorScheme,
} from "./themePreference";
import { getStoredTheme, setStoredTheme } from "./themeStorage";

/** Current OS / browser appearance as a concrete light or dark value. */
function readSystemScheme(): ResolvedColorScheme {
    return Appearance.getColorScheme() === "dark" ? "dark" : "light";
}

/**
 * Value to pass to NativeWind `setColorScheme`.
 *
 * On native, `setColorScheme` calls `Appearance.setColorScheme`, which overrides
 * the OS appearance for the whole app. Passing explicit light/dark while the
 * preference is "system" freezes the theme until a cold start — so native keeps
 * `"system"` as-is.
 *
 * On web with `darkMode: 'class'`, `setColorScheme('system')` clears the `.dark`
 * class and desyncs CSS from JS hooks — so web always maps to light/dark.
 */
function colorSchemeForNativeWind(
    preference: AppThemePreference,
): AppThemePreference | ResolvedColorScheme {
    if (Platform.OS === "web") {
        return resolveColorScheme(preference, readSystemScheme());
    }
    return preference;
}

const ThemePreferenceContext = createContext<{
    preference: AppThemePreference;
    changeTheme: (theme: AppThemePreference) => Promise<void>;
    systemScheme: ResolvedColorScheme;
} | null>(null);

/**
 * Owns the user's theme preference (light / dark / system), persists it, and
 * keeps NativeWind's color scheme in sync across platforms.
 */
export function ThemePreferenceProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    // NativeWind setter that toggles CSS variables / `.dark` class.
    const { setColorScheme } = useColorScheme();

    // Stored user choice; defaults to system until AsyncStorage hydrates.
    const [preference, setPreference] = useState<AppThemePreference>("system");
    // Live OS scheme — used for UI labels when preference is "system".
    const [systemScheme, setSystemScheme] =
        useState<ResolvedColorScheme>(readSystemScheme);

    // Refs so Appearance / AppState listeners always see the latest values
    // without re-subscribing on every render.
    const preferenceRef = useRef(preference);
    const setColorSchemeRef = useRef(setColorScheme);

    // Keep preferenceRef aligned with state for listeners.
    useEffect(() => {
        preferenceRef.current = preference;
    });

    // Keep setColorSchemeRef aligned with NativeWind's setter.
    useEffect(() => {
        setColorSchemeRef.current = setColorScheme;
    });

    /** Push the given preference into NativeWind (platform-aware mapping). */
    const applyPreference = useCallback((next: AppThemePreference) => {
        setColorSchemeRef.current(colorSchemeForNativeWind(next));
    }, []);

    // Hydrate preference from AsyncStorage on mount, then apply it.
    useEffect(() => {
        let cancelled = false;

        void (async () => {
            const stored = await getStoredTheme();
            if (cancelled) {
                return;
            }

            const nextPreference = stored ?? "system";
            setPreference(nextPreference);
            applyPreference(nextPreference);
        })();

        return () => {
            cancelled = true;
        };
    }, [applyPreference]);

    // Follow OS appearance changes (and re-check on foreground) so systemScheme
    // stays current; on web also re-apply NativeWind when preference is system.
    useEffect(() => {
        /** Update systemScheme state; on web with "system", re-apply NativeWind. */
        const syncFromSystem = (scheme: ColorSchemeName) => {
            const nextSystem = scheme === "dark" ? "dark" : "light";
            setSystemScheme(nextSystem);

            // Native: NativeWind follows Appearance when preference is "system".
            // Web: we must push an explicit light/dark for class-based dark mode.
            if (preferenceRef.current === "system" && Platform.OS === "web") {
                setColorSchemeRef.current(nextSystem);
            }
        };

        syncFromSystem(Appearance.getColorScheme() ?? "light");

        const appearanceSub = Appearance.addChangeListener(
            ({ colorScheme }) => {
                syncFromSystem(colorScheme ?? "light");
            },
        );

        // iOS often delivers the Appearance change only after returning foreground.
        const appStateSub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                syncFromSystem(Appearance.getColorScheme() ?? "light");
            }
        });

        return () => {
            appearanceSub.remove();
            appStateSub.remove();
        };
    }, []);

    /** Persist a new preference and apply it immediately. */
    const changeTheme = useCallback(
        async (theme: AppThemePreference) => {
            await setStoredTheme(theme);
            setPreference(theme);
            applyPreference(theme);
        },
        [applyPreference],
    );

    return (
        <ThemePreferenceContext.Provider
            value={{
                preference,
                changeTheme,
                systemScheme,
            }}
        >
            {children}
        </ThemePreferenceContext.Provider>
    );
}

/** Read theme preference context; must be used under ThemePreferenceProvider. */
export function useThemePreference() {
    const ctx = useContext(ThemePreferenceContext);
    if (!ctx) {
        throw new Error("useThemePreference outside ThemePreferenceProvider");
    }
    return ctx;
}
